/**
 * The three path rules that the chrome, the shell and the surface host share, so that all three
 * agree on what a path is (kernel apps, WS-13 section 3.2). Each function is pure.
 *
 * `isNavigablePath` moves from the shell's `services/location-service.ts`, `isUnderMount` from
 * `logic/app-loader-utils.ts` and `appHref` from `home/logic/apps-launcher-utils.ts`. Each keeps
 * its behaviour unchanged.
 */

/**
 * True when `text` holds a C0 control character or DEL. A browser's URL parser strips tab, LF
 * and CR before it resolves dot segments, so any of them can spell a different path than the one
 * the caller checked.
 */
function hasControlCharacter(text: string): boolean {
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * True when `to` is an absolute same-origin path that a link or a surface may navigate to.
 *
 * Callers: `chromeHref` before it builds any link, the sidebar before it draws an item, and the
 * surface host's location authority before a surface navigates.
 *
 * @param to Any value, typically a registry row field or a surface's argument.
 * @returns `true` for a string that starts with one `/` and not `//`, with no backslash and no
 *   C0 control or DEL character anywhere. `false` for every other value, a non-string included.
 *   A scheme (`https:`, `javascript:`) cannot start such a string, so the function refuses it
 *   too. It never throws.
 */
export function isNavigablePath(to: unknown): to is string {
  return (
    typeof to === 'string' &&
    to.startsWith('/') &&
    !to.startsWith('//') &&
    !to.includes('\\') &&
    !hasControlCharacter(to)
  );
}

/**
 * True when `pathname` falls under `mountPath` on a path-segment boundary.
 *
 * Callers: `matchAppByPath`, the header's active-app labels, the sidebar's `aria-current`, and
 * the surface host's `stripMount`.
 *
 * Rules, in order:
 * 1. An empty `mountPath` is a data error and matches nothing. Untreated, it degrades to the
 *    prefix `/` and every path matches (#2139).
 * 2. Both sides fold to lower case, so the answer agrees with react-router, which matches without
 *    case. `/ADMIN/users` is under `/admin`.
 * 3. One trailing slash on the mount is removed, except on the root mount `/`. The mount grammar
 *    (`MOUNT_PATH_GRAMMAR_PATTERN` in `@zion/sdk-contracts`) forbids a trailing slash, and the
 *    shell re-validates nothing on read, so an older row can still carry one.
 * 4. The pathname equals the mount, or starts with the mount and a `/`. `/docs` holds `/docs`
 *    and `/docs/intro`, and `/docs-archive` stays outside it. The root mount `/` holds every path.
 *
 * `stripMount()` in `@zion/sdk-react` shares rules 2 to 4 as a transform. The two must agree;
 * when they differ, this function wins, because it resolves the route. A change to one belongs
 * in both. It never throws.
 */
export function isUnderMount(pathname: string, mountPath: string): boolean {
  if (mountPath === '') return false;
  const p = pathname.toLowerCase();
  let mount = mountPath.toLowerCase();
  if (mount.length > 1 && mount.endsWith('/')) mount = mount.slice(0, -1);
  if (p === mount) return true;
  const prefix = mount.endsWith('/') ? mount : `${mount}/`;
  return p.startsWith(prefix);
}

/**
 * The deep link of an app: the manifest `mountPath` joined with its `defaultRoute`.
 *
 * Callers: `createAdminAppEntry` for the admin tile's route, the header's Profile link, and the
 * shell's Home launcher grid.
 *
 * Both inputs are relative to the shell, with a leading slash on `mountPath`. The answer is:
 * - `/` when `mountPath` trims to empty;
 * - `mountPath` (trimmed) when `defaultRoute` trims to empty or `/`;
 * - otherwise the two joined with exactly one slash, so the result never holds `//` at the join.
 *
 * It does not check the result with {@link isNavigablePath}; the caller of a link does. It never
 * throws.
 */
export function appHref(mountPath: string, defaultRoute: string): string {
  const base = mountPath.trim();
  if (base === '') return '/';
  const route = defaultRoute.trim();
  if (route === '' || route === '/') return base;
  const left = base.endsWith('/') ? base.slice(0, -1) : base;
  const right = route.startsWith('/') ? route : `/${route}`;
  return `${left}${right}`;
}
